
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, User, ChevronDown, Crown, History, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import UserSubscriptionDetails from './UserSubscriptionDetails';

interface AuthButtonProps {
  isPremium?: boolean;
  isAdmin?: boolean;
}

const AuthButton = ({ isPremium = false, isAdmin = false }: AuthButtonProps) => {
  const { user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    const { error } = await signOut();
    toast({
      title: "Sesión cerrada",
      description: "Has cerrado sesión correctamente",
    });
  };

  if (loading) {
    return <div className="w-24 h-10 bg-gray-200 animate-pulse rounded-lg" />;
  }

  if (user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className={`flex items-center gap-2 ${
              isPremium 
                ? 'border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700' 
                : 'border-green-300 bg-green-50 hover:bg-green-100 text-green-700'
            }`}
          >
            {isPremium && <Crown className="w-4 h-4 text-amber-500" />}
            <User className={`w-4 h-4 ${isPremium ? 'text-amber-600' : 'text-green-600'}`} />
            <span className="hidden sm:inline font-medium max-w-[120px] truncate">
              {user.user_metadata?.full_name || user.email}
            </span>
            <ChevronDown className={`w-4 h-4 ${isPremium ? 'text-amber-600' : 'text-green-600'}`} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-fit p-0">
          <div className="p-2">
            <UserSubscriptionDetails />
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/history')} className="cursor-pointer">
            <History className="w-4 h-4 mr-2" />
            Historial
            {!isPremium && <Crown className="h-3 w-3 ml-1 text-amber-500" />}
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem onClick={() => navigate('/configuration')} className="cursor-pointer">
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="text-red-600 focus:text-red-600 cursor-pointer">
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button 
      onClick={() => navigate('/auth')}
      className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
    >
      <LogIn className="w-4 h-4" />
      Iniciar sesión
    </Button>
  );
};

export default AuthButton;
